import api from '@/services/api.service';

function unwrap<T>(res: unknown): T {
  const body = (res as { data?: unknown })?.data ?? res;
  return (body as { data?: T })?.data ?? (body as T);
}

export interface WorkshopJobRow {
  case_id: string;
  case_number: string;
  title: string;
  case_status: string;
  case_type: string;
  priority: string;
  asset_id: string;
  asset_code?: string | null;
  asset_name?: string | null;
  assigned_employee_id?: string | null;
  cost_responsibility?: string | null;
  execution_id?: string | null;
  execution_stage?: string | null;
  hold_reason?: string | null;
  qc_state?: string | null;
  routing_mode?: string | null;
  facility_profile_id?: string | null;
  company_location_id?: string | null;
  intake_at?: string | null;
  execution_version?: number | null;
  execution_created_at?: string | null;
}

export interface WorkshopJobWorkspace {
  case: Record<string, unknown> & {
    id: string;
    case_number: string;
    title: string;
    description?: string | null;
    status: string;
    case_type?: string;
    priority: string;
    asset_id: string;
    assigned_employee_id?: string | null;
    cost_responsibility?: string | null;
    hire_agreement_id?: string | null;
    version?: number;
    asset?: { id: string; asset_code?: string; asset_name?: string };
  };
  active_execution: WorkshopExecution | null;
  executions: WorkshopExecution[];
  stage_history: Array<{
    id: string;
    from_stage?: string | null;
    to_stage: string;
    from_hold?: string | null;
    to_hold?: string | null;
    reason?: string | null;
    changed_by?: string | null;
    changed_at: string;
  }>;
  labour: WorkshopLabourLine[];
  breakdown?: Record<string, unknown> | null;
  pmv_profile?: { id: string; operational_status: string; asset_id: string } | null;
}

export interface WorkshopExecution {
  id: string;
  maintenance_case_id: string;
  routing_mode: string;
  leg_status: string;
  execution_stage: string;
  hold_reason?: string | null;
  hold_set_at?: string | null;
  company_location_id?: string | null;
  facility_profile_id?: string | null;
  intake_at?: string | null;
  intake_notes?: string | null;
  intake_condition_notes?: string | null;
  diagnosis_notes?: string | null;
  qc_state: string;
  qc_waived_reason?: string | null;
  release_authorized_at?: string | null;
  release_target_status?: string | null;
  version: number;
  created_at?: string;
}

export interface WorkshopLabourLine {
  id: string;
  employee_id: string;
  role_code: string;
  trade_code?: string | null;
  started_at?: string | null;
  ended_at?: string | null;
  actual_hours?: number | null;
  work_performed?: string | null;
  line_status: string;
}

export interface MaintenanceResponsibility {
  code: string;
  maintenance_responsibility?: string | null;
  agreement_id?: string | null;
  ownership_type?: string | null;
  commercial_status?: string | null;
  message?: string;
}

export interface PassportWorkshopStrip {
  case?: { id: string; case_number: string; title: string; status: string } | null;
  execution?: WorkshopExecution | null;
  stage_label?: string | null;
  hold_label?: string | null;
}

const workshopService = {
  async listJobs(params?: {
    stage?: string;
    hold_reason?: string;
    case_status?: string;
    case_type?: string;
  }): Promise<WorkshopJobRow[]> {
    const res = await api.get('/workshop/jobs', { params });
    return unwrap<WorkshopJobRow[]>(res) ?? [];
  },

  async getJob(caseId: string): Promise<WorkshopJobWorkspace> {
    const res = await api.get(`/workshop/jobs/${caseId}`);
    return unwrap<WorkshopJobWorkspace>(res);
  },

  async openExecution(body: {
    maintenance_case_id: string;
    routing_mode?: string;
    company_location_id?: string | null;
    facility_profile_id?: string | null;
  }) {
    const res = await api.post('/workshop/executions', body);
    return unwrap(res);
  },

  async recordIntake(
    executionId: string,
    body: {
      intake_notes?: string;
      intake_condition_notes?: string;
      diagnosis_notes?: string;
      meter?: {
        meter_type?: string;
        meter_unit?: string;
        reading_value: number;
        pmv_profile_id: string;
      };
    }
  ) {
    const res = await api.post(`/workshop/executions/${executionId}/intake`, body);
    return unwrap(res);
  },

  async changeStage(executionId: string, body: { to_stage: string; reason?: string; expected_version?: number }) {
    const res = await api.post(`/workshop/executions/${executionId}/stage`, body);
    return unwrap(res);
  },

  async applyHold(executionId: string, body: { hold_reason: string; reason?: string }) {
    const res = await api.post(`/workshop/executions/${executionId}/holds`, body);
    return unwrap(res);
  },

  async clearHold(executionId: string) {
    const res = await api.delete(`/workshop/executions/${executionId}/holds`);
    return unwrap(res);
  },

  async assignLead(executionId: string, body: { employee_id: string }) {
    const res = await api.post(`/workshop/executions/${executionId}/lead`, body);
    return unwrap(res);
  },

  async startLabour(
    executionId: string,
    body: { employee_id: string; role_code?: string; trade_code?: string; work_performed?: string }
  ) {
    const res = await api.post(`/workshop/executions/${executionId}/labour`, body);
    return unwrap(res);
  },

  async closeLabour(labourId: string, body?: { work_performed?: string; ended_at?: string }) {
    const res = await api.patch(`/workshop/labour/${labourId}/close`, body ?? {});
    return unwrap(res);
  },

  async listParts(caseId: string) {
    const res = await api.get(`/workshop/jobs/${caseId}/parts`);
    return unwrap(res);
  },

  async linkPartsRequest(caseId: string, material_request_id: string) {
    const res = await api.post(`/workshop/jobs/${caseId}/parts/link-request`, {
      material_request_id,
    });
    return unwrap(res);
  },

  async markWorkComplete(executionId: string, body?: { expected_version?: number }) {
    const res = await api.post(`/workshop/executions/${executionId}/work-complete`, body ?? {});
    return unwrap(res);
  },

  async recordQc(executionId: string, body: { result: 'PASS' | 'FAIL'; notes?: string; expected_version?: number }) {
    const res = await api.post(`/workshop/executions/${executionId}/qc`, body);
    return unwrap(res);
  },

  async waiveQc(executionId: string, body: { reason: string; expected_version?: number }) {
    const res = await api.post(`/workshop/executions/${executionId}/qc/waive`, body);
    return unwrap(res);
  },

  async authorizeRelease(
    executionId: string,
    body: {
      release_target_status?: 'AVAILABLE' | 'OUT_OF_SERVICE';
      notes?: string;
      expected_version?: number;
    }
  ) {
    const res = await api.post(`/workshop/executions/${executionId}/release`, body);
    return unwrap(res);
  },

  async listFacilities() {
    const res = await api.get('/workshop/facilities');
    return unwrap(res);
  },

  async createFacility(body: Record<string, unknown>) {
    const res = await api.post('/workshop/facilities', body);
    return unwrap(res);
  },

  async getResponsibility(pmvProfileId: string): Promise<MaintenanceResponsibility> {
    const res = await api.get('/workshop/responsibility', {
      params: { pmv_profile_id: pmvProfileId },
    });
    return unwrap<MaintenanceResponsibility>(res);
  },

  async createHireException(caseId: string, body: { hire_agreement_id: string; reason: string }) {
    const res = await api.post(`/workshop/jobs/${caseId}/hire-exception`, body);
    return unwrap(res);
  },

  async passportStrip(pmvProfileId: string): Promise<PassportWorkshopStrip> {
    const res = await api.get(`/workshop/passport/${pmvProfileId}/strip`);
    return unwrap<PassportWorkshopStrip>(res);
  },
};

export default workshopService;
